/**
 * Koľko karta práve teraz naozaj platí.
 *
 * Sinbadove VOYAGE sú vytlačené na 2, ale s tromi ďalšími v odhode útočia za 5.
 * V papierovej hre si hráč odhod jednoducho spočíta; tu to musí povedať UI,
 * inak vyzerá efekt ako pokazený.
 *
 * Je to **len náhľad** — o skutočnej hodnote rozhoduje `resolveCombat`. Preto sa
 * počíta zo stavu, ktorý má klient k dispozícii, a keď efekt siahne po niečom,
 * čo tam nie je, vráti sa vytlačená hodnota.
 */

import type { Ctx } from '../ops.js';
import type { CardId, ContentIndex, FighterId, GameState, PlayerIndex } from '../types.js';
import { EFFECTS } from './index.js';

export interface CardValue {
  /** Hodnota vytlačená na karte. */
  base: number;
  /** Hodnota po efektoch „počas súboja". */
  value: number;
}

export function effectiveValue(
  state: GameState,
  content: ContentIndex,
  player: PlayerIndex,
  cardId: CardId,
  fighterId?: FighterId,
): CardValue {
  const instance = state.cards[cardId];
  const def = instance ? content.cards[instance.defId] : undefined;
  if (!instance || !def) return { base: 0, value: 0 };

  const during = EFFECTS[instance.defId]?.during;
  if (!during) return { base: def.value, value: def.value };

  const ctx: Ctx = { state, content, events: [] };
  // Ktorá figúrka kartu zahrá, môže hodnotu meniť (Momentous Shift). Bez
  // zadanej figúrky berieme najlepší prípad zo všetkých, ktoré ju smú zahrať —
  // hráč sa pýta „koľko to môže dať".
  const candidates = fighterId
    ? [fighterId]
    : state.fighters
        .filter((f) => f.owner === player && (!def.character || f.defId === def.character))
        .map((f) => f.id);

  let best = def.value;
  for (const candidate of candidates.length > 0 ? candidates : [null]) {
    try {
      const value = during(
        {
          ctx,
          cardId,
          player,
          fighterId: candidate,
          role: 'attacker',
          opponentPlayer: null,
          opponentFighterId: null,
          won: false,
        },
        def.value,
      );
      if (value > best) best = value;
    } catch {
      // Efekt si vypýtal niečo, čo v tomto stave nie je — náhľad to nezhodí.
    }
  }
  return { base: def.value, value: best };
}
